import { useMemo, useState, useEffect, useRef } from 'react'
import { countAnnotationSets } from '../utils/annotationSets'
import { T } from '../theme'

const D     = '#1a1916'
const GOLD  = '#4F8EF7'
const CREAM = '#f5f2eb'
const MUTED = 'rgba(245,242,235,0.4)'
const RULE  = 'rgba(245,242,235,0.12)'
const MONO  = T.mono

// Couleurs alignees avec backend /api/moments/types/
const TYPE_COLORS = {
  goal:                '#4F8EF7',
  chance:              '#f59e0b',
  loss:                '#dc2626',
  recovery:            '#16a34a',
  transition_for:      '#3b82f6',
  transition_against:  '#991b1b',
  defensive_phase:     '#6b7280',
  set_piece:           '#a855f7',
  tactical_note:       '#9ca3af',
  player_action:       '#6366f1',
}

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00'
  const total = Math.floor(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  if (h > 0) return `${String(h).padStart(2, '0')}:${mm}:${ss}`
  return `${mm}:${ss}`
}

/**
 * Timeline visuelle des moments sous le player.
 *
 * @prop {Array}    moments        liste des moments du match
 * @prop {number}   duration       duree totale de la video en secondes
 * @prop {number}   currentTime    position actuelle du player en secondes
 * @prop {function} onMomentClick  appele avec (moment) au clic sur un marker
 * @prop {function} onSeek         appele avec (seconds) pour deplacer la
 *                                 lecture (tap/drag sur la barre). Optionnel :
 *                                 sans cette prop la timeline reste read-only.
 *
 * Mobile (< 768px) : barre fine 8px (au lieu de 32px) avec une zone
 * tactile transparente de 44px (Apple HIG) pour le scrub au doigt. Les
 * boutons de saut rapide -60/-10/+10/+60 + saisie de temps vivent dans
 * VideoPlayer (deck de controles consolide), pas ici, pour eviter un
 * double champ de saisie.
 */
export default function Timeline({ moments = [], duration = 0, currentTime = 0, onMomentClick, onSeek }) {

  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  const barRef = useRef(null)
  const draggingRef = useRef(false)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Position en pourcentage du curseur de lecture
  const cursorPos = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0

  // Pre-calcul des positions des markers
  const markers = useMemo(() => {
    if (duration <= 0) return []
    return moments.map(m => ({
      ...m,
      leftPct: Math.min(100, Math.max(0, (m.timecode_start / duration) * 100)),
      color: TYPE_COLORS[m.moment_type] || MUTED,
      annotated: countAnnotationSets(m) > 0,
    }))
  }, [moments, duration])

  const canSeek = typeof onSeek === 'function' && duration > 0
  const barHeight = isMobile ? 8 : 32
  // Hit-target 44px centre sur la barre, quelle que soit sa hauteur.
  const hitInset = -((44 - barHeight) / 2)

  const seekFromClientX = (clientX) => {
    if (!canSeek || !barRef.current) return
    const rect = barRef.current.getBoundingClientRect()
    if (rect.width <= 0) return
    const pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    onSeek(pct * duration)
  }

  const handlePointerDown = (e) => {
    if (!canSeek) return
    draggingRef.current = true
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* noop */ }
    seekFromClientX(e.clientX)
  }
  const handlePointerMove = (e) => {
    if (!draggingRef.current) return
    seekFromClientX(e.clientX)
  }
  const handlePointerUp = (e) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* noop */ }
  }

  return (
    <div style={{
      background: D,
      borderTop: `1px solid ${RULE}`,
      padding: '14px 16px 16px',
    }}>

      {/* Header — reste visible au-dessus de la barre */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 10,
      }}>
        <span style={{
          fontFamily: MONO, fontSize: 9, letterSpacing: '.18em',
          textTransform: 'uppercase', color: GOLD,
        }}>
          Timeline · {moments.length} moment{moments.length > 1 ? 's' : ''}
        </span>
        {duration > 0 && (
          <span style={{
            fontFamily: MONO, fontSize: 9, letterSpacing: '.05em', color: MUTED,
          }}>
            {formatTime(duration)}
          </span>
        )}
      </div>

      {/* Barre de timeline (8px mobile / 32px desktop) */}
      <div
        ref={barRef}
        style={{
          position: 'relative',
          height: barHeight,
          background: 'rgba(245,242,235,0.04)',
          border: `1px solid ${RULE}`,
        }}
      >

        {/* Zone tactile transparente 44px (Apple HIG) centree sur la barre,
            zIndex 1 sous les markers (zIndex 2) pour ne pas voler leurs
            taps. tap-to-seek + drag-to-scrub. */}
        {canSeek && (
          <div
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{
              position: 'absolute',
              left: 0, right: 0,
              top: hitInset, bottom: hitInset,
              zIndex: 1,
              cursor: 'pointer',
              touchAction: 'none',
            }}
          />
        )}

        {/* Markers */}
        {markers.map(m => (
          <button
            key={m.id}
            onClick={() => onMomentClick && onMomentClick(m)}
            title={`${formatTime(m.timecode_start)} · ${m.moment_type}${m.annotated ? ' · annoté' : ''}${m.note ? ' — ' + m.note : ''}`}
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${m.leftPct}%`,
              // Un marqueur annote est plus large et coiffe d'un lisere clair :
              // il se distingue au premier coup d'oeil sans dependre de la
              // couleur, qui porte deja le type du moment.
              width: m.annotated ? 10 : 6,
              transform: 'translateX(-50%)',
              background: m.color,
              border: 'none',
              borderTop: m.annotated ? `4px solid ${T.surface}` : 'none',
              boxShadow: m.annotated ? `0 0 0 1px ${m.color}` : 'none',
              cursor: 'pointer',
              padding: 0,
              zIndex: m.annotated ? 3 : 2,
              transition: 'all .15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.width = m.annotated ? '13px' : '8px'
              e.currentTarget.style.boxShadow = `0 0 8px ${m.color}`
            }}
            onMouseLeave={e => {
              e.currentTarget.style.width = m.annotated ? '10px' : '6px'
              e.currentTarget.style.boxShadow = m.annotated ? `0 0 0 1px ${m.color}` : 'none'
            }}
          />
        ))}

        {/* Curseur de lecture */}
        {duration > 0 && (
          <div style={{
            position: 'absolute',
            top: -2,
            bottom: -2,
            left: `${cursorPos}%`,
            width: 2,
            transform: 'translateX(-50%)',
            background: CREAM,
            pointerEvents: 'none',
            zIndex: 3,
            boxShadow: '0 0 4px rgba(245,242,235,0.6)',
          }} />
        )}

        {/* Etat vide — masque sur la barre fine mobile (pas la place) */}
        {markers.length === 0 && !isMobile && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: MONO, fontSize: 9, letterSpacing: '.15em',
            textTransform: 'uppercase', color: MUTED,
            pointerEvents: 'none',
          }}>
            Aucun moment marque
          </div>
        )}
      </div>
    </div>
  )
}
